import React, { FunctionComponent, Fragment, MouseEvent, ChangeEvent, useState, useEffect } from 'react';
import clsx from 'clsx';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { Input } from 'components/UI/V3/Forms/Input/Input';
import { Room, RoomUnit } from '@spike/room-model';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLayerGroup } from '@fortawesome/pro-solid-svg-icons';
import { faPaw } from '@fortawesome/pro-light-svg-icons';
import styles from './SearchDropDown.module.scss';
import { RoomListItem } from '@spike/appointments-list-model';
import { useDispatch } from 'react-redux';
import { showWarningThunk } from '@spike/notifications-action';

interface Props {
    selected: RoomListItem | null;
    rooms: Array<Room>;
    onSelect: (room: RoomListItem | null, unitId: number | undefined) => void;
    className?: string;
    loading?: boolean;
    x: number;
    y: number;
}

const SearchDropDown: FunctionComponent<Props> = props => {
    const dispatch = useDispatch();
    const [searchText, setSearchText] = useState('');
    const [filteredRoom, setFilteredRoom] = useState<Array<Room>>(props.rooms);
    const SIZE_MISMATCH_WARNING_TEXT = 'This room does not allow pets of this size.';

    const isBySizeRoom = (room: Room): boolean =>
        room.units.some(unit => unit.sizeEligibility !== null && unit.sizeEligibility !== undefined);

    const isBySizeInvalidUnit = (unit: RoomUnit): boolean =>
        unit.sizeEligibility === 'mismatch' || unit.sizeEligibility === 'inconsistent';

    const match = (room: Room, text: string): boolean => {
        const tokens = text.split(' ');
        return !tokens.map(token => token.toLowerCase()).some(t => !room.name.toLowerCase().includes(t));
    };

    const compareRoom = (a: Room, b: Room) => {
        if (a.id === -1) return -1;
        if (b.id === -1) return 1;
        return a.name.localeCompare(b.name);
    };

    const search = (text: string) => {
        setFilteredRoom([...props.rooms.filter(r => match(r, text))].sort(compareRoom));
    };

    const windowHeight = window.innerHeight;
    const dropDownDirection = props.y / windowHeight > 0.75 ? 'up' : 'down';
    const containerStyle =
        dropDownDirection === 'down'
            ? { left: props.x, top: props.y }
            : { left: props.x, bottom: windowHeight - props.y - 40 };

    useEffect(() => {
        search(searchText);
    }, [props.rooms]);

    useEffect(() => {
        const timer = setTimeout(() => search(searchText), 100);
        return () => clearTimeout(timer);
    }, [searchText]);

    const clickHandler = (e: MouseEvent<HTMLElement>) => {
        e.preventDefault(), e.stopPropagation();
    };

    const inputChangeHandler = (e: ChangeEvent<HTMLInputElement>) => setSearchText(e.target.value);

    const convertRoomToRoomListItem = (room: Room, roomUnitId: number): RoomListItem => {
        const unit = room.units.find(u => u.id === roomUnitId)!;
        return {
            id: room.id!,
            name: room.name,
            unit: {
                id: unit.id!,
                name: unit.name
            }
        };
    };

    const searchBox = (
        <div className={styles.searchBoxContainer}>
            <Input
                className={styles.text}
                placeholder={'Unit Name'}
                startIcon={<FontAwesomeIcon icon={faLayerGroup} />}
                onChange={inputChangeHandler}
                autoFocus
            />
        </div>
    );

    const elements = (
        <Fragment>
            <div
                key="unassigned"
                className={clsx(styles.elementContainer)}
                onClick={() => props.onSelect?.(null, undefined)}
            >
                <span className={styles.text}>Unassigned</span>
            </div>
            {filteredRoom.map(room => {
                const capacityRule = room.rules.find(r => r.ruleTypeId === 1);
                const capacity = capacityRule ? JSON.parse(capacityRule.data).quantity : undefined;
                const roomHasBySize = isBySizeRoom(room);

                return (
                    <div
                        key={room.uuid}
                        className={styles.roomGroup}
                    >
                        <div className={clsx(styles.text, styles.roomTitle)}>{room.name}</div>
                        {room.units.map(unit => {
                            const remainingCapacity =
                                capacity !== undefined ? capacity - (unit.occupancy ?? 0) : undefined;
                            const isFull = remainingCapacity !== undefined && remainingCapacity < 1;
                            const isUnavailable = !unit.active;
                            const isBySizeMismatch = isBySizeInvalidUnit(unit);

                            const handleClick = () => {
                                if (isUnavailable) {
                                    return;
                                }

                                if (isBySizeMismatch) {
                                    dispatch(showWarningThunk(SIZE_MISMATCH_WARNING_TEXT));
                                } else if (roomHasBySize && !unit.available) {
                                    dispatch(
                                        showWarningThunk('This room is already at full capacity for the selected time')
                                    );
                                } else if (isFull) {
                                    dispatch(
                                        showWarningThunk('This room is already at full capacity for the selected time')
                                    );
                                }

                                props.onSelect?.(convertRoomToRoomListItem(room, unit.id!), unit.id);
                            };

                            return (
                                <div
                                    key={unit.uuid}
                                    className={clsx(styles.elementContainer, {
                                        [styles.fullCapacity]: isFull,
                                        [styles.unavailable]: isUnavailable,
                                        [styles.selectedUnit]: props.selected?.unit.id === unit.id
                                    })}
                                    onClick={handleClick}
                                >
                                    <span className={styles.text}>{unit.name}</span>
                                    <div className={styles.capacityInfo}>
                                        {isUnavailable ? (
                                            <span className={styles.unavailableText}>Unavailable</span>
                                        ) : isBySizeMismatch ? (
                                            <span className={styles.unavailableText}>By Size</span>
                                        ) : roomHasBySize ? (
                                            <span className={styles.unavailableText}>
                                                {unit.available ? 'Available' : 'Unavailable'}
                                            </span>
                                        ) : (
                                            <>
                                                <FontAwesomeIcon
                                                    icon={faPaw}
                                                    className={styles.capacityIcon}
                                                />
                                                <span className={styles.text}>
                                                    {remainingCapacity ?? unit?.occupancy ?? '0'}
                                                    {capacity ? `/${capacity}` : ''}
                                                </span>
                                            </>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                );
            })}
        </Fragment>
    );

    const elementsNotFound = (
        <div className={styles.elementContainer}>
            <span className={styles.text}>Room not found.</span>
        </div>
    );

    const spinner = (
        <div className={clsx(styles.elementContainer, styles.center)}>
            <div className={styles.spinner} />
        </div>
    );

    const list = (
        <OverlayScrollbarsComponent style={{ height: '130px', marginBottom: '6px' }}>
            <div className={styles.listContainer}>
                {filteredRoom.length > 0 && !props.loading && elements}
                {filteredRoom.length === 0 && !props.loading && elementsNotFound}
                {props.loading && spinner}
            </div>
        </OverlayScrollbarsComponent>
    );

    const dropDown = (
        <div className={styles.dropdownContainer}>
            {searchBox}
            {list}
        </div>
    );

    return (
        <div
            className={clsx(styles.container, props.className)}
            onClick={clickHandler}
            style={containerStyle}
        >
            {dropDown}
        </div>
    );
};

export default SearchDropDown;
